import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import { action, extendObservable, reaction } from 'mobx';
import { urlSimpleSchemeValidator } from '../../../urlPatternValidator';
import { emailValidator } from '../../../email';
import { apiCall } from '../../../API';
import AsyncActionStore from '../../AsyncActionStore';
import InputStore from '../../InputStore';
import RadioButtonStore from '../../RadioButtonStore';

export const POLICY_TESTING_CHANNELS = {
    TAP_URL_ISOLATION: 'URL_ISOLATION',
    BROWSER_EMAIL_ISOLATION: 'BROWSING_DEFENSE',
    BROWSER_EMAIL_REGISTRATION: 'BROWSER_EMAIL_REGISTRATION',
    CATEGORY_LOOKUP: 'CATEGORY_LOOKUP',
};

export default class PolicyTestingStore {
    static PropType = PropTypes.shape({
        activeTab: RadioButtonStore.PropType.isRequired,
        requestUrl: InputStore.PropType.isRequired,
        userEmail: InputStore.PropType.isRequired,
        senderEmail: InputStore.PropType.isRequired,
        fetchResults: PropTypes.func.isRequired,
        onChangeTab: PropTypes.func.isRequired,
        requestUrlError: PropTypes.string,
        policyEvaluateStatus: AsyncActionStore.PropType.isRequired,
        results: MobxPropTypes.objectOrObservableObject,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class PolicyTestingStore */{
            activeTab: new RadioButtonStore(POLICY_TESTING_CHANNELS.TAP_URL_ISOLATION),
            requestUrl: new InputStore(),
            userEmail: new InputStore(),
            senderEmail: new InputStore(),
            requestUrlError: '',
            apiPolicyEvaluate: apiCall('/console/policy_testing/evaluate'),
            policyEvaluateStatus: new AsyncActionStore(),
            apiCustomCategoriesList: apiCall('/console/custom-categories/match'),
            apiStandardCategoriesList: apiCall('/console/categories?includeHighRisk=true'),
            results: null,
        }, data);

        this.requestUrl.setValidator(urlSimpleSchemeValidator);
        this.userEmail.setValidator(emailValidator);
        this.senderEmail.setValidator(emailValidator);

        reaction(() => this.requestUrl.value, () => {
            this.requestUrlError = '';
        });
    }

    fetchResults = action(() => {
        let params = { email: this.userEmail.getValue().toLowerCase() };
        if (!this.activeTab.equals(POLICY_TESTING_CHANNELS.BROWSER_EMAIL_REGISTRATION)) {
            const requestUrl = this.requestUrl.getValue();
            if (!/^https?:\/\//i.test(requestUrl)) {
                this.requestUrlError = 'Specify http:// or https:// at the beginning of the URL';
                return Promise.resolve();
            }

            if (this.activeTab.equals(POLICY_TESTING_CHANNELS.CATEGORY_LOOKUP)) {
                return this.fetchCategories();
            }

            params = {
                ...params,
                url: requestUrl,
                senderEmail: this.senderEmail.getValue().toLowerCase(),
                channel: this.activeTab.getValue(),
                scopes: this.activeTab.equals(POLICY_TESTING_CHANNELS.TAP_URL_ISOLATION)
                    ? ['redirect', 'exclude', 'content']
                    : ['exclude', 'content'],
            };
        }
        if (!this.policyEvaluateStatus.isLoading) {
            this.policyEvaluateStatus.setLoading(this.apiPolicyEvaluate(params).then(action((data) => {
                this.results = data || {};
                return data;
            })));
        }
        return this.policyEvaluateStatus.promise;
    });

    fetchCategories = action(() => {
        if (!this.policyEvaluateStatus.isLoading) {
            const params = { value: this.requestUrl.getValue() };
            const promises = Promise.all([
                this.apiStandardCategoriesList(params),
                this.apiCustomCategoriesList(params)
            ]);

            this.policyEvaluateStatus.setLoading(promises.then(action((data) => {
                this.results = { standardCategories: data[0] || [], customCategories: data[1] || [] };
                return { standardCategories: data[0] || [], customCategories: data[1] || [] };
            })));
        }
        return this.policyEvaluateStatus.promise;
    });

    onChangeTab = action(() => {
        this.results = null;
    });
}
